import React, { useMemo } from 'react'
import { TextField } from 'foundations-library/components'
import { TextFieldType } from 'foundations-library/types'
import { required as requiredValidationRule, zip, postalCode } from 'validation-rules'
interface ZipCodeTextFieldProps extends TextFieldType {
    countryCode: string,
    withPlaceholder?: boolean,
    withLabel?: boolean
    validate?: boolean
}

/**
 * Addaptation of zip-code-text-field - uses new components and interfaces
 *
 * Textfield component that provides format-as-you type functionality for entering a zip
 * or postal code (depending on the specified country)
 */
const ZipCodeTextField = ({
    countryCode,
    onChange,
    withPlaceholder = true,
    withLabel = false,
    required = false,
    validate = false,
    ...props
}: ZipCodeTextFieldProps): JSX.Element => {
    const liveFormatPostalCode = (postalCode: string) => {
        postalCode = postalCode.replace(/[^a-zA-Z\d\s:]/g, '')
        let formatedPostalCode = ''
        let isLastNumeric = true
        Array.from(postalCode).forEach(char => {
            if (formatedPostalCode.length === 7) return
            if (isLastNumeric) {
                if (/^\d+$/.test(char)) return
            } else {
                if (/^[a-zA-Z]+$/.test(char)) return
            }
            if (formatedPostalCode.length === 3) formatedPostalCode += ' '
            if (char === ' ') return
            formatedPostalCode += char.toUpperCase()
            if (formatedPostalCode.length === 3 && window.prevPostalCode?.length === 2) formatedPostalCode += ' '
            isLastNumeric = !isLastNumeric
        })
        window.prevPostalCode = formatedPostalCode
        return formatedPostalCode
    }

    const onTextFieldChange = v => {
        if (!onChange) return null
        let formatted = ''
        if (countryCode === 'CA') {
            formatted = liveFormatPostalCode(v).slice(0, 7)
        } else {
            formatted = v.replace(/[^0-9-]/g, '').slice(0, 10)
        }
        onChange(formatted)
    }

    const placeholderOrLabel = useMemo(() => countryCode === 'CA' ? 'Postal code' : 'Zip code', [countryCode])

    const placeholder = useMemo(() => {
        if (Object.prototype.hasOwnProperty.call(props, 'placeholder')) {
            return props.placeholder
        }
        if (withPlaceholder && !withLabel) {
            return placeholderOrLabel
        }

        return undefined
    }, [withLabel, withPlaceholder, props.placeholder, placeholderOrLabel])

    const label = useMemo(() => {
        if (Object.prototype.hasOwnProperty.call(props, 'label')) {
            return props.label
        }
        if (withLabel) {
            return placeholderOrLabel
        }

        return undefined
    }, [withLabel, props.placeholder, placeholderOrLabel])

    const rules = useMemo(() => {
        const rs = []
        if (required) rs.push(requiredValidationRule())
        if (validate) rs.push(countryCode === 'US' ? zip() : postalCode())

        return rs
    }, [countryCode, validate, required])

    return (
        <TextField
            {...props}
            rules={rules}
            placeholder={placeholder}
            label={label}
            maxLength={countryCode === 'CA' ? 7 : 10}
            onChange={onTextFieldChange}
            key={`${countryCode}-${validate.toString()}-${required.toString()}`}
        />
    )
}

export default ZipCodeTextField
